import WarningAmber from "@mui/icons-material/WarningAmber";
import {Box, Skeleton, Switch, Typography} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import React, {Dispatch, useEffect, useMemo,useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import { colors } from "../../../../../../config/colors";
import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data";
import {
	getCompanyAbsenceManagementLocationSettings,
	resetCreateTimeOffType,
	resetDeleteTimeOffType,
	resetEditTimeOffType, setAbsenceManagementSelectedLocations, setAbsenceManagementToggle,
} from "../../../../store/gat-admin-slice";
import AllowedLocations
	from "./allowed-locations";
import LeaveTypeForm, {
	LeaveTypeFormValues,
} from "./ui/dialog/leave-type-form";
import AttendanceManagementTable from "./ui/table";
import LeaveBalanceUpdate from "./update-balance";

interface LeaveType extends LeaveTypeFormValues {
	enabledLocations: { id: number }[];
	incrementHours: number;
	applyBeforeHours: number;
	locs: { id: number; name: string }[];
}

interface Location {
	id: string;
	name: string;
}

interface LocationSettings {
	locationId: number;
	value: boolean;
}

interface AbsenceManagementProps {
	companyId: number;
	newAddOnSettings: any
	setNewAddOnSettings: Dispatch<any>;
	loading: boolean;
}

const AttendanceManagement: React.FC<AbsenceManagementProps> = ({
	companyId,
	newAddOnSettings,
	setNewAddOnSettings,
	loading,
}) => {
	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<"add" | "edit">("add");
	const [initialData, setInitialData] = useState<LeaveTypeFormValues | null>(
		null
	);

	const dispatch = useDispatch();

	const selectedLocations = useSelector(
		(state: any) => state.gatAdminConfiguration.updateCompanyAbsenceManagementLocationSettings.selectedLocations,
	);
	const toggle = useSelector(
		(state: any) => state.gatAdminConfiguration.updateCompanyAbsenceManagementLocationSettings.toggle,
	);
	const companyAbsenceManagementLocationSettings = useSelector(
		(state: any) => state.gatAdminConfiguration.getCompanyAbsenceManagementLocationSettings.locationSettings,
	);

	const {
		createTimeOffTypeStatus,
		editTimeOffTypeStatus,
		createTimeOffTypeErrorMessage,
		editTimeOffTypeErrorMessage,
		deleteTimeOffTypeErrorMessage,
		deleteTimeOffTypeStatus,
		getCompanyDataStatus,
		updateCompanySettingsStatus,
		locations
	} = useGatAdminConfigData();

  const activeLocations = useMemo(() => {
    return locations?.filter((location: any) => location.isActive);
  },[locations]);

	const handleOpenAdd = () => {
		setMode("add");
		setInitialData(null);
		setOpen(true);
	};

	const handleOpenEdit = (leaveTypeData: LeaveType) => {
		setMode("edit");
		setInitialData(leaveTypeData);
		setOpen(true);
	};

	const handleClose = () => setOpen(false);

	useEffect(() => {
		if (toggle) {
			dispatch(getCompanyAbsenceManagementLocationSettings({ companyId }));
		}
	}, [companyId, dispatch, toggle]);

	useEffect(() => {
		try {
			dispatch(setAbsenceManagementToggle(newAddOnSettings?.absenceManagementEnabled));
		} catch (e) {
			// Do nothing
		}
	}, [dispatch, newAddOnSettings?.absenceManagementEnabled]);

	useEffect(() => {
		try {
			if (companyAbsenceManagementLocationSettings?.length > 0) {
				const enabledLocations = companyAbsenceManagementLocationSettings.filter(
					(location: LocationSettings) => location.value
				)?.map((location: LocationSettings) => location?.locationId);
				const selectedLocations = activeLocations.filter((location: any) =>
					enabledLocations?.includes(location.id)
				).map((location: any) => ({
					id: location.id,
					name: location.name,
				}));
				dispatch(setAbsenceManagementSelectedLocations(selectedLocations));
			} else {
				dispatch(setAbsenceManagementSelectedLocations([]));
			}
		} catch (e) {
			// Do nothing
		}
	}, [companyAbsenceManagementLocationSettings, dispatch, activeLocations]);

	useEffect(() => {
		try {
			const selectedLocationsId = selectedLocations?.map((location: any) => location.id);
			const companyAbsenceManagementLocationSettingsId = companyAbsenceManagementLocationSettings?.filter((location: any) => location.value)?.map((location: any) => location.locationId);
			const sortedSelectedLocations = selectedLocationsId?.sort();
			const sortedCompanyAbsenceManagementLocationSettings = companyAbsenceManagementLocationSettingsId?.sort();
			if ( sortedSelectedLocations?.length > 0 &&
				JSON.stringify(sortedSelectedLocations) !== JSON.stringify(sortedCompanyAbsenceManagementLocationSettings)) {
				setNewAddOnSettings((pre: any) => ({
					...pre,
					absenceManagementEnabled: true,
				}));
			}
		} catch (e) {
			// Do nothing
		}
	}, [companyAbsenceManagementLocationSettings, selectedLocations, setNewAddOnSettings]);


	const handleLocationChange = (
		event: any
	) => {
		const value = event.target.value;
		const selectedAll = value[value.length - 1] === "all";
		if (selectedAll) {
			if (selectedLocations?.length !== activeLocations.length) {
				dispatch(setAbsenceManagementSelectedLocations(activeLocations));
			} else {
				dispatch(setAbsenceManagementSelectedLocations([]));
			}
			return;
		}
		// Filter out the selected locations
		const filteredLocations = activeLocations.filter((location: Location) =>
			value.includes(location.id)
		);
		dispatch(setAbsenceManagementSelectedLocations(filteredLocations));
	};

	const handleDelete = (
		event: React.MouseEvent,
		locationToDelete: string
	) => {
		event.stopPropagation();
		dispatch(
			setAbsenceManagementSelectedLocations(
				selectedLocations.filter(
					(location: { id: string }) => location.id !== locationToDelete
				)
			)
		);
	};

	return (
		<Box
			sx={{
				backgroundColor: "#F5F5F5",
				borderRadius: "8px",
				marginTop: "32px",
				marginBottom: "16px",
				padding: "16px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "16px",
							color: "rgba(0, 0, 0, 0.87)",
						}}
					>
						Absence Management
					</Typography>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "12px",
							color: "rgba(0, 0, 0, 0.6)",
						}}
					>
						Enable Workers to time off in Wilya as per Company Absence Policy
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{loading || getCompanyDataStatus === "pending" ? (
							<Skeleton width={50} height={40} />
						) :
						<Switch
							checked={toggle ?? false}
							onChange={(e) => {
								dispatch(setAbsenceManagementToggle(e.target.checked));
								if (!e.target.checked) {
									setNewAddOnSettings((pre: any) => ({
										...pre,
										absenceManagementEnabled: e.target.checked,
									}));
								}
							}}
						/>
					}
				</Box>
			</Box>
			{toggle ? (
				<Box
					sx={{
						marginTop: "20px",
						marginBottom: "8px",
						borderRadius: "8px",
						border: 1,
						borderWidth: "1px",
						borderColor: "rgba(0,0,0,0.12)",
						background: "#FFF",
						padding: "16px",
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
						}}
					>
						{activeLocations?.length > 0 ?
							<AllowedLocations
								loading={
									loading
								}
								selectedLocations={selectedLocations}
								handleLocationChange={handleLocationChange}
								handleDelete={handleDelete}
								locations={activeLocations}
								updateStatus={updateCompanySettingsStatus}
							/> : <Typography
								sx={{
									fontFamily: "Roboto",
									fontWeight: 400,
									fontSize: "12px",
									color: "rgba(0, 0, 0, 0.6)",
								}}>
								Please add a location from the Locations tab to be able to select it for Absence Management.
							</Typography>
						}
					</Box>
					{activeLocations?.length > 0 && selectedLocations.length === 0 ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: "4px",
								marginLeft: "8px",
								marginTop: "4px",
							}}
						>
							<WarningAmber sx={{ color: colors.error, fontSize: "14px" }} />
							<Typography
								sx={{
									fontFamily: "Roboto",
									fontWeight: 400,
									fontSize: "12px",
									color: colors.error,
								}}
							>
								Select at least 1 location to save.
							</Typography>
						</Box>
					) : null}
				</Box>
			) : null}

			{toggle ? (
				<AttendanceManagementTable
					handleOpenEdit={handleOpenEdit}
					handleOpenAdd={handleOpenAdd}
				/>
			) : null}

      {toggle ? (
        <LeaveBalanceUpdate
          newAddOnSettings={newAddOnSettings}
          setNewAddOnSettings={setNewAddOnSettings}
          disabled={selectedLocations.length === 0}
        />
      ) : null}

			<LeaveTypeForm
				open={open}
				onClose={handleClose}
				mode={mode}
				initialData={initialData}
			/>

			{/* Add Skills for worker */}
			<Snackbar
				open={createTimeOffTypeStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetCreateTimeOffType())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Time Off added successfully
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={createTimeOffTypeStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetCreateTimeOffType())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{createTimeOffTypeErrorMessage ?? "An error occurred"}
				</MuiAlert>
			</Snackbar>

			{/* Edit Skill for worker */}
			<Snackbar
				open={editTimeOffTypeStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetEditTimeOffType())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Time Off edited successfully
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={editTimeOffTypeStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetEditTimeOffType())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{editTimeOffTypeErrorMessage ?? "An error occurred"}
				</MuiAlert>
			</Snackbar>

			{/* Delete Skills for worker */}
			<Snackbar
				open={deleteTimeOffTypeStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetDeleteTimeOffType())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Time Off Deactivated successfully
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={deleteTimeOffTypeStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetDeleteTimeOffType())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{deleteTimeOffTypeErrorMessage ?? "An error occurred"}
				</MuiAlert>
			</Snackbar>
		</Box>
	);
};

export default AttendanceManagement;
